/**
 * The comment name's pure rules and copy — commentHub only, shared by both brands' account pages
 * (`NameCard`, `NameEditor`, `NameSource`). Lifted from SoundGuys' `lib/community/settings.ts`,
 * which re-exports them so its hub Settings keeps one source.
 *
 * The display-name rules themselves (trim, `DISPLAY_NAME_MAX`, the refusal copy) are NOT here:
 * they live in `lib/profile-name.ts`, shared with the sign-up name step (Constraint 077b9e8e5).
 * `commentNameCanSave` takes the max from there; this file only adds the editor's "is this a
 * change" rule and the source labels.
 */
import type { CommentHubProfile } from 'shared-code/commenthub-api'

export const NAME_COPY = {
  commentsLabel: 'Article comments',
  nameInherited: 'From Google · updates automatically',
  nameInheritedLinked: 'From your linked account',
  nameCustom: 'Your own name',
  nameNote: 'Type your own name to stop syncing from Google.',
  nameNoteLinked: 'Type your own name to stop syncing from your linked account.',
  nameReset: 'Use my Google name',
} as const

/**
 * Whether the inherited name and photo are Google's: any linked Google identity — NOT
 * `identities[0]`, which commentHub orders by last login and so flips with the method used last.
 * Unknown (no identities) keeps the Google wording — every account before magic-link sign-in is
 * Google-first.
 */
export function inheritsFromGoogle(
  identities: ReadonlyArray<{ provider: string }> | null | undefined
): boolean {
  return !identities?.length || identities.some(i => i.provider === 'google')
}

/**
 * The provider name the comment name falls back to: the Google identity's when one is linked
 * (the same rule as `inheritsFromGoogle`, so label and value agree), else the first identity's.
 */
export function inheritedIdentityName(
  identities: ReadonlyArray<{ provider: string; name: string | null }> | null | undefined
): string | null {
  const google = identities?.find(i => i.provider === 'google')
  return (google ?? identities?.[0])?.name ?? null
}

/** The comments name card's line (commentHub `displayNameSource`); `google` = `inheritsFromGoogle`. */
export function commentNameLine(source: 'inherited' | 'custom', google = true): string {
  if (source === 'custom') {
    return NAME_COPY.nameCustom
  }
  return google ? NAME_COPY.nameInherited : NAME_COPY.nameInheritedLinked
}

/**
 * Whether the comment-name editor's draft is a change worth a PATCH: non-empty, at most `max`,
 * not the name already shown — and, while the name is inherited, not the Google name itself
 * (saving it would silently make it custom and stop the sync).
 */
export function commentNameCanSave(
  draft: string,
  shown: string,
  googleName: string | null,
  custom: boolean,
  max: number
): boolean {
  const trimmed = draft.trim()
  return (
    trimmed !== '' &&
    trimmed.length <= max &&
    trimmed !== shown &&
    !(!custom && googleName !== null && trimmed === googleName)
  )
}

/** The commentHub name as commentHub shows it (custom, else the Google identity's). */
export function commentName(profile: CommentHubProfile): string {
  return profile.user.displayName ?? inheritedIdentityName(profile.identities) ?? ''
}

/** "October 2026" from commentHub's `createdAt`; empty when it cannot be read. */
export function memberSince(iso: string | null | undefined): string {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}
